import React from 'react';
import { Instagram, Eye, Clock, Heart, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import TiktokIcon from '@/components/ui/TiktokIcon';

export default function VideoCard({ video, isSelected, onSelect }) {
  const platformIcons = {
    instagram: <Instagram className="h-3 w-3 sm:h-4 sm:w-4" />,
    tiktok: <TiktokIcon className="h-3 w-3 sm:h-4 sm:w-4" />
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <Card
      className={`
        group relative cursor-pointer
        backdrop-blur-sm bg-white/60 dark:bg-gray-900/50
        hover:bg-white/80 dark:hover:bg-gray-800/60
        border border-white/20 dark:border-white/10
        hover:border-purple-200 dark:hover:border-purple-800
        rounded-xl overflow-hidden
        transform transition-all duration-300 ease-out
        hover:scale-[1.02] hover:-translate-y-0.5
        hover:shadow-xl hover:shadow-purple-500/20
        ${isSelected ? 'ring-2 ring-purple-500 scale-[1.02] -translate-y-0.5' : ''}
      `}
      onClick={() => onSelect(video.id)}
    >
      <CardContent className="p-0">
        <div className="relative">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <div className="pb-[177.77%]"> {/* 9:16 aspect ratio */}
            <img
              src={video.cover_image_url}
              alt={`Video ${video.id}`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Platform badges with glassmorphism */}
          <div className="absolute top-2 left-2 flex gap-1.5">
            <Badge className="
              backdrop-blur-md bg-black/30 border border-white/20
              text-white text-[10px] sm:text-xs
              px-2 py-1 rounded-full
              flex items-center gap-1.5
              shadow-lg shadow-black/20
            ">
              {platformIcons[video.platform]}
              <span className="hidden sm:inline">{video.platform}</span>
            </Badge>
          </div>

          {/* Posted badges */}
          <div className="absolute top-2 right-2 flex flex-col gap-1.5">
            {video.posted_platforms?.map(platform => (
              <Badge 
                key={platform}
                className="
                  backdrop-blur-md bg-green-500/30 border border-green-400/30
                  text-white text-[10px] sm:text-xs
                  px-2 py-1 rounded-full
                  flex items-center gap-1.5
                  shadow-lg shadow-green-500/30
                "
              >
                {platformIcons[platform]}
                <span className="hidden sm:inline">Posted</span>
              </Badge>
            ))}
          </div>

          {/* Selected badge with animation */}
          {isSelected && (
            <div className="absolute bottom-2 right-2 animate-fade-in">
              <Badge className="
                backdrop-blur-md bg-purple-500/50 border border-purple-400/30
                text-white text-[10px] sm:text-xs
                px-2 py-1 rounded-full
                shadow-lg shadow-purple-500/30
              ">
                Selected
              </Badge>
            </div>
          )}

          {/* Backed Up badge */}
          {video.is_downloaded && (
            <div className="absolute bottom-2 right-2 group">
              <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/90 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Backed Up
              </span>
              <Badge className="
                backdrop-blur-md bg-green-500/30 border border-green-400/30
                text-white text-[10px] sm:text-xs
                px-2 py-1 rounded-full
                flex items-center gap-1.5
                shadow-lg shadow-green-500/30
              ">
                <Check className="h-3 w-3" />
              </Badge>
            </div>
          )}

          {/* Metrics Overlay */}
          <div className="absolute bottom-3 left-2 flex flex-col gap-1.5 text-white">
            {video.platform === 'instagram' ? (
              <>
                <div className="flex items-center gap-1.5">
                  <Heart className="h-3.5 w-3.5 drop-shadow-lg" fill="white" />
                  <span className="text-sm font-medium drop-shadow-lg">{formatNumber(video.metrics?.likes)}</span>
                </div>
                {video.metrics?.comments > 0 && (
                  <div className="flex items-center gap-1.5">
                    <svg className="h-3.5 w-3.5 drop-shadow-lg" fill="white" viewBox="0 0 24 24">
                      <path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z" />
                    </svg>
                    <span className="text-sm font-medium drop-shadow-lg">{formatNumber(video.metrics?.comments)}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5 drop-shadow-lg" />
                  <span className="text-sm font-medium drop-shadow-lg">{formatNumber(video.view_count)}</span>
                </div>
                {video.metrics?.likes > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Heart className="h-3.5 w-3.5 drop-shadow-lg" fill="white" />
                    <span className="text-sm font-medium drop-shadow-lg">{formatNumber(video.metrics?.likes)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Stats overlay on hover */}
          <div className="
            absolute inset-x-0 bottom-0
            backdrop-blur-md bg-black/30
            p-2 sm:p-3
            transform translate-y-full group-hover:translate-y-0
            transition-transform duration-300 ease-out
          ">
            <div className="flex items-center justify-between text-[10px] sm:text-xs text-white/90">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {video.platform === 'instagram' ? (
                    <>
                      <Heart className="h-3 w-3 text-pink-400" />
                      <span>{video.metrics?.likes || 0}</span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3 text-blue-400" />
                      <span>{video.view_count || 0}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-purple-400" />
                <span>
                  {(() => {
                    const seconds = Math.round(video.duration || 0);
                    if (seconds >= 60) {
                      const minutes = Math.floor(seconds/60);
                      const remainingSeconds = seconds % 60;
                      return `${minutes}m ${remainingSeconds}s`;
                    }
                    return `${seconds}s`;
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}